import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { ICategoryProfileResponse } from 'pages/supplier-intelligence/utils/types';
import type { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/supplier-intelligence/categories/profile';

type TData = ICategoryProfileResponse;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { categoryId: number; depth: number }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSiCategoryProfileApi(
  categoryId: number | null,
  depth: number | null,
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, { params: { categoryId, depth } });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint, { categoryId: categoryId!, depth: depth! }], queryFn, {
    enabled: isLoggedIn && categoryId != null && depth != null,
    ...options,
  });
}
